import { cva } from 'class-variance-authority';

import { cn } from '@heidi/utils';

import {
  controlChevron,
  controlFocus,
  controlInvalid,
  controlSurface,
  controlSurfaceHover,
  controlTransition,
  controlValueText,
} from '../styles/control';
import { formFieldErrorText } from '../styles/form-field';

/**
 * Experimental Combobox styles, behind `release-experimental-design-system-forms`.
 * Content stays `p-0` and the gutter comes from the shared `CommandGroup`'s
 * `p-1`, so the search divider is full-bleed without a negative-margin escape.
 */

/** Transparent so the dropdown reads on the popover's `surface-elevated`. */
export const comboboxCommandClassesExperimental = 'bg-transparent';

/** Trigger text from the tier; the Button opts out of its own typography. */
export const comboboxTriggerTextClassesExperimental = controlValueText;

/**
 * Colour only: a span state, not `::placeholder`, so it can't ride
 * `controlFieldText`. ComboboxTrigger always wraps children in its label slot;
 * the slot carries this colour directly, so Button hover/open text swaps do not
 * change it through inheritance.
 */
export const comboboxTriggerPlaceholderClassesExperimental = cn(
  'text-control-text-placeholder'
);

/**
 * The trigger's box, wearing the control tier over the Button underneath. These
 * land after `buttonVariantsExperimental` so tailwind-merge resolves each pair
 * down to ours.
 *
 * This is the only tier member composed on top of another component, so it is
 * the only one that has to *displace* a competing recipe rather than simply
 * state its own. Everything local below is one of those two jobs: undoing a
 * Button default the tier disagrees with (radius, fill swaps, the `aria-invalid`
 * ring width), or naming anatomy no other control has (`aria-expanded`, a
 * span-based placeholder, an error message). The shared constants carry the
 * rest.
 */
export const comboboxTriggerBoxClassesExperimental = cn(
  controlSurface,
  controlChevron,
  controlTransition,
  // The trigger takes Button `size="md"` for its 36px, which brings
  // `rounded-md` with it. The tier's comfortable radius is `rounded-lg` — Input
  // and Select both state it on their own recipe, so the trigger does too rather
  // than inheriting the Button's.
  'rounded-lg',
  // Fill swaps, listed separately from the open border below: they don't collide
  // with a border class, so tailwind-merge would keep the Button's alongside ours.
  'hover:bg-control-background active:bg-control-background aria-expanded:bg-control-background',
  // Spelled out rather than prefixed onto `controlSurfaceDisabled`: Tailwind's
  // scanner only sees literal class strings, so a computed prefix emits no CSS.
  'disabled:cursor-not-allowed disabled:border-control-border-disabled disabled:bg-control-background-disabled',
  'disabled:text-control-text-value-disabled [&_svg]:disabled:text-control-icon-disabled',
  controlSurfaceHover,
  controlFocus,
  // Displaces the `focusRingInvalidExperimental` inherited from the Button,
  // which still rings at the stable `ring-2 … /20`. Without this the
  // errored trigger would wear a different invalid shape to every other control.
  controlInvalid,
  // …and the width has to be reset separately. The tier gives invalid no ring of
  // its own — the halo is focus's — so nothing in `controlInvalid` displaces the
  // Button's `aria-invalid` width, and an unfocused errored trigger would keep
  // wearing a 2px halo no other control draws.
  //
  // Restored under focus at (0,3,0): `aria-[invalid]:ring-0` and
  // `focus-visible:ring-[1.5px]` are both (0,2,0), so a focused invalid trigger
  // would otherwise resolve on Tailwind's emission order and could lose its halo.
  "aria-[invalid='true']:ring-0",
  "aria-[invalid='true']:focus-visible:ring-[1.5px]",
  // Mirrors the focus halo while open, since Combobox moves DOM focus into the
  // command input after the trigger expands.
  'aria-expanded:z-10 aria-expanded:border-control-border-focus aria-expanded:ring-[1.5px] aria-expanded:ring-control-border-focus/60',
  'enabled:hover:aria-expanded:border-control-border-focus enabled:hover:aria-expanded:ring-control-border-focus/60',
  // `[&>svg]` leads so `aria-expanded` keys the trigger, not the glyph.
  '[&>svg]:aria-expanded:text-control-icon',
  // Restated under `aria-invalid` so an errored open trigger stays red, not focus-blue.
  "aria-[invalid='true']:aria-expanded:border-control-border-invalid",
  "aria-[invalid='true']:aria-expanded:ring-[1.5px]",
  "aria-[invalid='true']:aria-expanded:ring-control-border-invalid/60",
  "aria-[invalid='true']:enabled:hover:aria-expanded:border-control-border-invalid",
  "aria-[invalid='true']:enabled:hover:aria-expanded:ring-control-border-invalid/60"
);

/**
 * Drops the stable `opacity-50`, which would compound with `controlChevron`'s to
 * 25%. Written as `opacity-100` so the flag-off path is untouched.
 */
export const comboboxTriggerIconClassesExperimental = 'opacity-100';

/** Shared menu-item recipe (`rounded-md py-1.5`) via the experimental `CommandItem`, plus pointer states. */
export const comboboxItemVariantsExperimental = cva(
  'rounded-md hover:bg-surface-hover hover:text-text-primary active:bg-surface-pressed'
);

/** Error classes — the `semantic-text-destructive` role. */
export const comboboxTriggerErrorClassesExperimental = cn(
  'text-semantic-text-destructive'
);

export const comboboxErrorMessageClassesExperimental = cn('mt-1', formFieldErrorText);
